"use client";

import { useState } from "react";
import Image from "next/image";

const nodes = [
  {
    id: "next",
    label: "Next.js",
    x: 50,
    y: 16,
    detail: "Production-ready web applications",
  },
  {
    id: "react",
    label: "React",
    x: 50,
    y: 38,
    primary: true,
    detail: "Component-driven interfaces",
  },
  {
    id: "ui",
    label: "UI",
    x: 18,
    y: 62,
    detail: "Clear, usable product surfaces",
  },
  {
    id: "ts",
    label: "TypeScript",
    x: 50,
    y: 62,
    detail: "Type-safe frontend architecture",
  },
  {
    id: "api",
    label: "API",
    x: 82,
    y: 62,
    detail: "Frontend to backend integration",
  },
] as const;

const paths = [
  { id: "next-react", d: "M50 20 L50 32" },
  { id: "react-ui", d: "M44 42 L24 56" },
  { id: "react-ts", d: "M50 44 L50 56" },
  { id: "react-api", d: "M56 42 L76 56" },
] as const;

const ArchitectureVisual = () => {
  const [activeId, setActiveId] = useState<string>("react");
  const active = nodes.find((node) => node.id === activeId) ?? nodes[1];

  return (
    <div className="relative mx-auto w-full max-w-md">
      <div
        className="absolute -inset-6 rounded-[2.5rem] bg-[radial-gradient(circle_at_50%_30%,rgba(62,224,192,0.18),transparent_58%)] blur-2xl"
        aria-hidden="true"
      />
      <div className="relative overflow-hidden rounded-[2rem] border border-[#1f2933] bg-[#0c1118]/70 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(rgba(243,239,230,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(243,239,230,0.04) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
          aria-hidden="true"
        />

        <div className="relative h-[380px] sm:h-[420px]">
          <svg
            viewBox="0 0 100 100"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
          >
            {paths.map((path) => (
              <g key={path.id}>
                <path
                  d={path.d}
                  fill="none"
                  stroke="#1f2933"
                  strokeWidth="0.7"
                />
                <path
                  d={path.d}
                  fill="none"
                  stroke="#3ee0c0"
                  strokeWidth="0.45"
                  strokeOpacity="0.55"
                />
                <circle r="0.9" fill="#3ee0c0">
                  <animateMotion
                    dur="3.8s"
                    repeatCount="indefinite"
                    path={path.d}
                    begin={`${path.id.length * 0.18}s`}
                  />
                </circle>
              </g>
            ))}
          </svg>

          {nodes.map((node) => {
            const isActive = activeId === node.id;
            return (
              <button
                key={node.id}
                type="button"
                onMouseEnter={() => setActiveId(node.id)}
                onFocus={() => setActiveId(node.id)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border px-3 py-1.5 text-xs font-medium tracking-wide outline-none transition duration-300 focus-visible:ring-2 focus-visible:ring-[#3ee0c0] sm:px-3.5 sm:text-sm ${
                  node.id === "react"
                    ? "bg-[#3ee0c0] text-[#06110e] border-[#3ee0c0] shadow-[0_0_28px_rgba(62,224,192,0.35)]"
                    : isActive
                      ? "border-[#3ee0c0]/70 bg-[#10151c] text-white"
                      : "border-[#1f2933] bg-[#0c1118]/90 text-[#d5dde6] hover:border-[#3ee0c0]/50"
                }`}
                style={{ left: `${node.x}%`, top: `${node.y}%` }}
                aria-label={`${node.label}: ${node.detail}`}
              >
                {node.label}
              </button>
            );
          })}
        </div>

        <div className="relative min-h-[76px] border-t border-[#1f2933] px-5 py-4">
          <p className="text-sm font-medium text-white">{active.label}</p>
          <p className="mt-1 text-xs leading-relaxed text-[#9aa6b5]">
            {active.detail}
          </p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.24em] text-[#6f7b8a]">
            React · Next.js · TS
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureVisual;
